import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, X, Send, Clock, ChevronLeft, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert relative avatar URLs to absolute
const getAvatarUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${window.location.origin}${url}`;
};

/**
 * Coach floating chat bubble + popup.
 * - Badge = number of unique clients with unread messages (1 client = 1 notification)
 * - Popup shows a list of conversations (grouped by client), with per-client unread pips
 * - Click a client -> conversation view with reply compose (existing full page remains at /coach/messages)
 * - Hidden on /coach/messages (redundant)
 */
export default function CoachFloatingChat({ coachUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadClients, setUnreadClients] = useState(0);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyText, setReplyText] = useState('');
  const threadRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setUnreadClients(res.data?.unread_clients_count || 0);
    } catch (_) { /* silent */ }
  };

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const [msgsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/messages`, { params: { mark_read: false } }),
        axios.get(`${API}/coach/clients`)
      ]);
      setMessages(msgsRes.data || []);
      setClients(clientsRes.data || []);
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Open a specific client's conversation + mark that client's messages as read
  const openConversation = async (clientId) => {
    setSelectedClient(clientId);
    try {
      const res = await axios.get(`${API}/messages`, {
        params: { mark_read_from: clientId }
      });
      setMessages(res.data || []);
      fetchUnread();
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (open) {
      fetchOverview();
      setSelectedClient(null);
    }
  }, [open]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, selectedClient, loading]);

  useEffect(() => {
    if (open && window.innerWidth < 640) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Group messages by client (conversation list)
  const conversations = (() => {
    const grouped = {};
    messages.forEach((msg) => {
      const clientId = msg.from_user_id === coachUser.id ? msg.to_user_id : msg.from_user_id;
      if (!grouped[clientId]) {
        grouped[clientId] = { clientId, messages: [], lastMessage: null, unreadCount: 0 };
      }
      grouped[clientId].messages.push(msg);
      if (msg.from_user_id !== coachUser.id && msg.status === 'unread') {
        grouped[clientId].unreadCount += 1;
      }
      if (!grouped[clientId].lastMessage || new Date(msg.created_at) > new Date(grouped[clientId].lastMessage.created_at)) {
        grouped[clientId].lastMessage = msg;
      }
    });
    return Object.values(grouped).sort((a, b) =>
      new Date(b.lastMessage?.created_at) - new Date(a.lastMessage?.created_at)
    );
  })();

  const clientName = (cid) => {
    const c = clients.find((x) => x.id === cid);
    return c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email : 'Client';
  };

  const clientInitials = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (!c) return '?';
    const a = (c.first_name || '').charAt(0);
    const b = (c.last_name || '').charAt(0);
    return (a + b).toUpperCase() || (c.email || '?').charAt(0).toUpperCase();
  };

  const selectedConversation = selectedClient
    ? conversations.find((c) => c.clientId === selectedClient)
    : null;

  const sortedThread = selectedConversation
    ? [...selectedConversation.messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    : [];

  const handleSend = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedClient) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, null, {
        params: { to_user_id: selectedClient, body: replyText, category: 'Coach Reply' }
      });
      setReplyText('');
      await openConversation(selectedClient);
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Hide on the dedicated coach messages page
  if (location.pathname.startsWith('/coach/messages')) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="coach-floating-chat-bubble"
          onClick={() => setOpen(true)}
          aria-label={unreadClients > 0 ? `${unreadClients} clients with unread messages` : 'Open messages'}
          className="fixed bottom-6 right-6 z-50 group"
          style={{ animation: unreadClients > 0 ? 'chatPulse 2s ease-in-out infinite' : 'none' }}
        >
          <span className="hidden md:inline-block absolute right-[70px] top-1/2 -translate-y-1/2 bg-white text-gray-900 text-sm font-medium px-4 py-2 rounded-full shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gray-200">
            {unreadClients > 0
              ? `${unreadClients} client${unreadClients === 1 ? '' : 's'} waiting for you`
              : 'Messages'}
          </span>
          <div className="relative w-14 h-14 rounded-full bg-[#1a61e8] hover:bg-[#1553c9] text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95">
            <MessageSquare className="w-6 h-6" fill="currentColor" strokeWidth={0} />
            {unreadClients > 0 && (
              <span
                data-testid="coach-floating-chat-badge"
                className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center ring-2 ring-white"
              >
                {unreadClients > 9 ? '9+' : unreadClients}
              </span>
            )}
          </div>
        </button>
      )}

      {open && (
        <div
          data-testid="coach-floating-chat-popup"
          className="fixed z-[110] bg-white shadow-2xl border border-gray-200 flex flex-col
                     inset-x-0 top-0 h-[100dvh] sm:inset-auto sm:top-auto sm:bottom-6 sm:right-6 sm:w-[420px] sm:h-[600px]
                     sm:rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#1a61e8] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {selectedClient && (
                <button
                  data-testid="coach-chat-back"
                  onClick={() => setSelectedClient(null)}
                  className="p-1 rounded-full hover:bg-white/20"
                  aria-label="Back to list"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="w-8 h-8 rounded-full overflow-hidden bg-white/20 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 ring-2 ring-white/40">
                {coachUser?.avatar_url ? (
                  <img src={getAvatarUrl(coachUser.avatar_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span>{(coachUser?.first_name?.[0] || 'C').toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">
                  {selectedClient ? clientName(selectedClient) : 'Messages'}
                </div>
                <div className="text-xs text-blue-100 truncate">
                  {selectedClient
                    ? 'Reply directly'
                    : `${conversations.length} conversation${conversations.length === 1 ? '' : 's'}`}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                data-testid="coach-chat-expand"
                onClick={() => { setOpen(false); navigate('/coach/messages'); }}
                aria-label="Open full page"
                className="p-1.5 rounded-full hover:bg-white/20"
                title="Open full page"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button
                data-testid="coach-chat-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-1.5 rounded-full hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          {!selectedClient ? (
            // List view
            <div className="flex-1 overflow-y-auto bg-gray-50">
              {loading ? (
                <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <MessageSquare className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {conversations.map((conv) => {
                    const last = conv.lastMessage;
                    const preview = last ? last.body : '';
                    const isFromClient = last && last.from_user_id !== coachUser.id;
                    return (
                      <li key={conv.clientId}>
                        <button
                          data-testid={`coach-chat-convo-${conv.clientId}`}
                          onClick={() => openConversation(conv.clientId)}
                          className="w-full text-left p-3 hover:bg-white transition-colors flex items-start gap-3"
                        >
                          {/* Avatar */}
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1a61e8] text-white text-sm font-semibold flex items-center justify-center">
                            {clientInitials(conv.clientId)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`text-sm truncate ${conv.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>
                                {clientName(conv.clientId)}
                              </span>
                              <span className="text-[11px] text-gray-400 flex-shrink-0">
                                {last && formatTime(last.created_at)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <p className={`text-xs truncate ${conv.unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                {isFromClient ? '' : 'You: '}{preview}
                              </p>
                              {conv.unreadCount > 0 && (
                                <Badge className="bg-red-500 hover:bg-red-500 text-white text-[10px] px-1.5 py-0 h-5 min-w-[20px] flex-shrink-0 rounded-full">
                                  {conv.unreadCount}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            // Conversation view
            <>
              <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {sortedThread.map((m) => {
                  const isFromMe = m.from_user_id === coachUser.id;
                  return (
                    <div key={m.id} className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                      <div className="max-w-[85%]">
                        {!isFromMe && m.category && (
                          <div className="mb-1">
                            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                              {m.category}
                            </Badge>
                          </div>
                        )}
                        <div
                          className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                            isFromMe
                              ? 'bg-[#1a61e8] text-white rounded-br-md'
                              : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                          }`}
                        >
                          {m.body}
                        </div>
                        <div className={`flex items-center gap-1 mt-1 text-[10px] text-gray-400 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                          <Clock className="h-3 w-3" />
                          {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleSend} className="border-t bg-white p-3 flex-shrink-0">
                <div className="flex gap-2 items-end">
                  <Textarea
                    data-testid="coach-chat-reply-textarea"
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onFocus={() => {
                      setTimeout(() => {
                        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
                      }, 350);
                    }}
                    rows={2}
                    style={{ fontSize: '16px' }}
                    className="flex-1 resize-none min-h-[44px]"
                  />
                  <Button
                    data-testid="coach-chat-reply-send"
                    type="submit"
                    disabled={sending || !replyText.trim()}
                    className="bg-[#1a61e8] hover:bg-[#1553c9] h-11 w-11 p-0 flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes chatPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
      `}</style>
    </>
  );
}
